import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PcnModel } from './shared/common/viewmodels/pcn.model';

@Injectable({
  providedIn: 'root',
})
export class GlobalObservables {
  /* Immediate Execution Subjects (Standard) */
  public beforeAdditionalExplanationSaved = new Subject();
  public afterAdditionalExplanationSaved = new Subject();

  /* PCN Subscriptions */
  public onResetPcn = new Subject();
  public onPcnResetRequest = new Subject();
  public onPcnChanging = new Subject();
  public onPcnChanged = new Subject<PcnModel | null>();

  /* Reasons Subscriptions */
  public OnSelectedReasonsUpdated = new Subject(); // Fires when reasons update
  public onReasonsChanging = new Subject(); // Fires when a change to reasons starts
  public onReasonsChanged = new Subject(); // Fires when a change to reasons finishes

  /* Questions/Documents Subscriptions */
  public onQuestionsChanging = new Subject();
  public onQuestionsChanged = new Subject();

  // Fires when a question refresh finished but did not succeed.
  //
  // onQuestionsChanging locks the letter editor and the PCN block, and
  // onQuestionsChanged both releases them and regenerates the letter. There was
  // no third outcome, so a failed refresh had to pick one of two wrong ones:
  // emit onQuestionsChanged and let the letter regenerate from the answers that
  // were on screen before the failed fetch, or emit nothing and leave the
  // editor locked with no request outstanding.
  //
  // Subscribers to this release their busy state and show a recoverable
  // message. They must not regenerate the letter - the point of the event is
  // that the answers it would be generated from are not known to be current.
  public onQuestionsFailed = new Subject();
  public onDocumentsChanging = new Subject();
  public onDocumentsChanged = new Subject();

  /* Letter Subscriptions */
  public onLetterChanging = new Subject();
  public onLetterChanged = new Subject();
  public onLetterSaved = new Subject<void>();
  public onCustomLettersShow = new Subject();
  public onCustomLettersChanged = new Subject();
  public onCustomParagraphsShow = new Subject();
  public onCustomParagraphsChanged = new Subject();
  public onLetterTextTypeChanged = new Subject();
  public letterTemplateChanged = new Subject();

  public onDecisionSetAction = new Subject();
  public togglePcnPanel = new Subject();

  // Admin subscriptions
  public onPoliciesAnswersSetted = new Subject();

  // Add/remove custom letters and paragraphs
  public addCustomLetter = new Subject();
  public removeCustomLetter = new Subject();
  public addCustomParagraph = new Subject();
  public removeCustomParagraph = new Subject();

  // Push error to debug console
  public pushError = new Subject();

  /* Debounced Subjects (for optimized execution) */
  private debouncedOnQuestionsChanged = new Subject<void>();
  private debouncedOnReasonsChanged = new Subject<void>();
  private debouncedOnLetterChanged = new Subject<void>();

  constructor() {
    // Log when debounce starts and when it fires
    this.onQuestionsChanged.pipe(debounceTime(1000)).subscribe(() => {
      this.debouncedOnQuestionsChanged.next();
    });

    this.onReasonsChanged.pipe(debounceTime(1000)).subscribe(() => {
      this.debouncedOnReasonsChanged.next();
    });

    this.onLetterChanged.pipe(debounceTime(1000)).subscribe(() => {
      this.debouncedOnLetterChanged.next();
    });
  }

  /* Provide Access to Debounced Observables */
  getDebouncedOnQuestionsChanged() {
    return this.debouncedOnQuestionsChanged.asObservable();
  }

  getDebouncedOnReasonsChanged() {
    return this.debouncedOnReasonsChanged.asObservable();
  }

  getDebouncedOnLetterChanged() {
    return this.debouncedOnLetterChanged.asObservable();
  }
}
